<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  filterTypes: {
    type: Array,
    required: true,
  },
  isFolderView: {
    type: Boolean,
    default: false,
  },
  folderName: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'applyFilter',
  'updateFolder',
  'clearFilters',
  'close',
]);

const filters = defineModel({
  type: Array,
  default: [],
});

const { t } = useI18n();

const VIEW = {
  MAIN: 'main',
  STATUS: 'status',
  PRIORITY: 'priority',
  LABELS: 'labels',
  ASSIGNEE: 'assignee_id',
  TEAM: 'team_id',
  INBOX: 'inbox_id',
  CREATED_AT: 'created_at',
  LAST_ACTIVITY_AT: 'last_activity_at',
  ADVANCED: 'advanced',
};

const MANAGED_KEYS = [
  VIEW.STATUS,
  VIEW.PRIORITY,
  VIEW.LABELS,
  VIEW.ASSIGNEE,
  VIEW.TEAM,
  VIEW.INBOX,
  VIEW.CREATED_AT,
  VIEW.LAST_ACTIVITY_AT,
];

const DEFAULT_OPERATORS = {
  [VIEW.STATUS]: 'equal_to',
  [VIEW.PRIORITY]: 'equal_to',
  [VIEW.LABELS]: 'equal_to',
  [VIEW.ASSIGNEE]: 'equal_to',
  [VIEW.TEAM]: 'equal_to',
  [VIEW.INBOX]: 'equal_to',
  [VIEW.CREATED_AT]: 'days_before',
  [VIEW.LAST_ACTIVITY_AT]: 'days_before',
};

const DATE_PRESETS = [
  { labelKey: 'OLDER_THAN_7_DAYS', value: '7' },
  { labelKey: 'OLDER_THAN_30_DAYS', value: '30' },
  { labelKey: 'OLDER_THAN_90_DAYS', value: '90' },
  { labelKey: 'OLDER_THAN_365_DAYS', value: '365' },
];

const activeView = ref(VIEW.MAIN);
const searchQuery = ref('');
const folderNameLocal = ref(props.folderName);

watch(
  () => props.folderName,
  value => {
    folderNameLocal.value = value;
  }
);

const filterByKey = computed(() =>
  props.filterTypes.reduce((acc, ft) => {
    acc[ft.attributeKey] = ft;
    return acc;
  }, {})
);

const getFilterType = key => filterByKey.value[key] || {};

const hasValue = filter => {
  const value = filter?.values;
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') return Boolean(value.id);
  return value !== undefined && value !== null && value !== '';
};

const meaningfulFilters = computed(() => filters.value.filter(hasValue));

const findFilter = key =>
  meaningfulFilters.value.find(f => f.attributeKey === key);

const getOptionValue = key => {
  const filter = findFilter(key);
  const value = filter?.values;
  if (Array.isArray(value)) return value[0];
  return value;
};

const statusOptions = computed(() => getFilterType(VIEW.STATUS).options || []);
const priorityOptions = computed(
  () => getFilterType(VIEW.PRIORITY).options || []
);
const labelsOptions = computed(() => getFilterType(VIEW.LABELS).options || []);
const assigneeOptions = computed(
  () => getFilterType(VIEW.ASSIGNEE).options || []
);
const teamOptions = computed(() => getFilterType(VIEW.TEAM).options || []);
const inboxOptions = computed(() => getFilterType(VIEW.INBOX).options || []);

const selectedMulti = key => findFilter(key)?.values || [];

const selectedSingle = key => {
  const value = findFilter(key)?.values;
  if (!value) return null;
  if (!Array.isArray(value) && typeof value === 'object' && value.id)
    return value;
  if (Array.isArray(value) && value.length) return value[0];
  return null;
};

const selectedAssignee = computed(() => selectedSingle(VIEW.ASSIGNEE));
const selectedTeam = computed(() => selectedSingle(VIEW.TEAM));
const selectedInbox = computed(() => selectedSingle(VIEW.INBOX));

const selectedDatePreset = key => {
  const value = getOptionValue(key);
  return DATE_PRESETS.find(p => p.value === String(value));
};

const selectedCreatedAtPreset = computed(() =>
  selectedDatePreset(VIEW.CREATED_AT)
);
const selectedLastActivityPreset = computed(() =>
  selectedDatePreset(VIEW.LAST_ACTIVITY_AT)
);

const filteredOptions = options => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return options;
  return options.filter(o => o.name.toLowerCase().includes(query));
};

const filteredLabels = computed(() => filteredOptions(labelsOptions.value));
const filteredAssignees = computed(() =>
  filteredOptions(assigneeOptions.value)
);
const filteredTeams = computed(() => filteredOptions(teamOptions.value));
const filteredInboxes = computed(() => filteredOptions(inboxOptions.value));

const cleanFilters = nextFilters => nextFilters.filter(hasValue);

const removeFilter = key => {
  filters.value = filters.value.filter(f => f.attributeKey !== key);
};

const upsertFilter = (key, values, operator = DEFAULT_OPERATORS[key]) => {
  const filterType = getFilterType(key);
  const existing = filters.value.find(f => f.attributeKey === key);

  if (!hasValue({ values })) {
    removeFilter(key);
    return;
  }

  const nextFilter = {
    attributeKey: key,
    attributeModel: filterType.attributeModel || 'standard',
    filterOperator: operator,
    queryOperator: existing?.queryOperator || 'and',
    values,
  };

  filters.value = cleanFilters([
    ...filters.value.filter(f => f.attributeKey !== key),
    nextFilter,
  ]);
};

const isMultiSelected = (key, option) =>
  selectedMulti(key).some(v => v.id === option.id);

const toggleMulti = (key, option) => {
  const current = selectedMulti(key);
  const next = isMultiSelected(key, option)
    ? current.filter(v => v.id !== option.id)
    : [...current, { id: option.id, name: option.name }];
  upsertFilter(key, next);
};

const setSingle = (key, option) => {
  upsertFilter(key, { id: option.id, name: option.name });
  activeView.value = VIEW.MAIN;
};

const setDatePreset = (key, preset) => {
  upsertFilter(key, preset.value);
  activeView.value = VIEW.MAIN;
};

const formatDatePreset = preset => {
  if (!preset) return '';
  return t(`FILTER.MOBILE.DATE_PRESETS.${preset.labelKey}`);
};

const unknownFilters = computed(() =>
  meaningfulFilters.value.filter(f => !MANAGED_KEYS.includes(f.attributeKey))
);

const formatMultiSummary = key =>
  selectedMulti(key)
    .map(v => v.name)
    .join(', ');

const summaries = computed(() =>
  [
    {
      key: VIEW.STATUS,
      label: t('FILTER.ATTRIBUTES.STATUS'),
      value: formatMultiSummary(VIEW.STATUS),
    },
    {
      key: VIEW.PRIORITY,
      label: t('FILTER.ATTRIBUTES.PRIORITY'),
      value: formatMultiSummary(VIEW.PRIORITY),
    },
    {
      key: VIEW.LABELS,
      label: t('FILTER.ATTRIBUTES.LABELS'),
      value: formatMultiSummary(VIEW.LABELS),
    },
    {
      key: VIEW.ASSIGNEE,
      label: t('FILTER.ATTRIBUTES.ASSIGNEE_NAME'),
      value: selectedAssignee.value?.name || '',
    },
    {
      key: VIEW.TEAM,
      label: t('FILTER.ATTRIBUTES.TEAM_NAME'),
      value: selectedTeam.value?.name || '',
    },
    {
      key: VIEW.INBOX,
      label: t('FILTER.ATTRIBUTES.INBOX_NAME'),
      value: selectedInbox.value?.name || '',
    },
    {
      key: VIEW.CREATED_AT,
      label: t('FILTER.ATTRIBUTES.CREATED_AT'),
      value: formatDatePreset(selectedCreatedAtPreset.value),
    },
    {
      key: VIEW.LAST_ACTIVITY_AT,
      label: t('FILTER.ATTRIBUTES.LAST_ACTIVITY'),
      value: formatDatePreset(selectedLastActivityPreset.value),
    },
  ].filter(item => item.value)
);

const categories = computed(() => [
  {
    key: VIEW.STATUS,
    icon: 'i-lucide-circle-dot',
    label: t('FILTER.ATTRIBUTES.STATUS'),
    summary: summaries.value.find(s => s.key === VIEW.STATUS)?.value,
  },
  {
    key: VIEW.PRIORITY,
    icon: 'i-lucide-flag',
    label: t('FILTER.ATTRIBUTES.PRIORITY'),
    summary: summaries.value.find(s => s.key === VIEW.PRIORITY)?.value,
  },
  {
    key: VIEW.LABELS,
    icon: 'i-lucide-tags',
    label: t('FILTER.ATTRIBUTES.LABELS'),
    summary: summaries.value.find(s => s.key === VIEW.LABELS)?.value,
  },
  {
    key: VIEW.ASSIGNEE,
    icon: 'i-lucide-user',
    label: t('FILTER.ATTRIBUTES.ASSIGNEE_NAME'),
    summary: summaries.value.find(s => s.key === VIEW.ASSIGNEE)?.value,
  },
  {
    key: VIEW.TEAM,
    icon: 'i-lucide-users',
    label: t('FILTER.ATTRIBUTES.TEAM_NAME'),
    summary: summaries.value.find(s => s.key === VIEW.TEAM)?.value,
  },
  {
    key: VIEW.INBOX,
    icon: 'i-lucide-inbox',
    label: t('FILTER.ATTRIBUTES.INBOX_NAME'),
    summary: summaries.value.find(s => s.key === VIEW.INBOX)?.value,
  },
  {
    key: VIEW.CREATED_AT,
    icon: 'i-lucide-calendar-plus',
    label: t('FILTER.ATTRIBUTES.CREATED_AT'),
    summary: summaries.value.find(s => s.key === VIEW.CREATED_AT)?.value,
  },
  {
    key: VIEW.LAST_ACTIVITY_AT,
    icon: 'i-lucide-activity',
    label: t('FILTER.ATTRIBUTES.LAST_ACTIVITY'),
    summary: summaries.value.find(s => s.key === VIEW.LAST_ACTIVITY_AT)?.value,
  },
  {
    key: VIEW.ADVANCED,
    icon: 'i-lucide-sliders-horizontal',
    label: t('FILTER.MOBILE.ADVANCED_FILTERS'),
    summary: unknownFilters.value.length
      ? t('FILTER.MOBILE.ACTIVE_COUNT', { count: unknownFilters.value.length })
      : '',
  },
]);

const resetSearchAndOpen = view => {
  searchQuery.value = '';
  activeView.value = view;
};

const clearAll = () => {
  filters.value = [];
  emit('clearFilters');
};

const applyFilters = () => {
  filters.value = cleanFilters(filters.value);

  if (!filters.value.length) {
    emit('clearFilters');
    emit('close');
    return;
  }

  if (props.isFolderView) {
    emit('updateFolder', filters.value, folderNameLocal.value);
  } else {
    emit('applyFilter', filters.value);
  }
};
</script>

<template>
  <div class="flex h-full min-h-0 flex-col md:h-[680px]">
    <main class="min-h-0 flex-1 overflow-y-auto pb-4">
      <template v-if="activeView === VIEW.MAIN">
        <div v-if="isFolderView" class="mb-5">
          <Input
            v-model="folderNameLocal"
            :label="t('FILTER.FOLDER_LABEL')"
            :placeholder="t('FILTER.INPUT_PLACEHOLDER')"
          />
        </div>

        <section v-if="summaries.length || unknownFilters.length" class="mb-6">
          <h4 class="mb-3 text-sm font-medium text-n-slate-11">
            {{ t('FILTER.MOBILE.ACTIVE_FILTERS') }}
          </h4>
          <div
            class="flex flex-col overflow-hidden rounded-xl border border-n-weak"
          >
            <div
              v-for="summary in summaries"
              :key="summary.key"
              class="flex items-center gap-3 border-b border-n-weak px-3 py-3 last:border-b-0"
            >
              <div class="min-w-0 flex-1">
                <p class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ summary.label }}
                </p>
                <p class="mb-0 truncate text-sm text-n-slate-11">
                  {{ summary.value }}
                </p>
              </div>
              <Button
                icon="i-lucide-x"
                color="slate"
                variant="ghost"
                size="sm"
                type="button"
                class="!rounded-full"
                @click="removeFilter(summary.key)"
              />
            </div>
            <div
              v-if="unknownFilters.length"
              class="flex items-center gap-3 px-3 py-3"
            >
              <div class="min-w-0 flex-1">
                <p class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ t('FILTER.MOBILE.ADVANCED_ACTIVE') }}
                </p>
                <p class="mb-0 text-sm text-n-slate-11">
                  {{
                    t('FILTER.MOBILE.ACTIVE_COUNT', {
                      count: unknownFilters.length,
                    })
                  }}
                </p>
              </div>
              <Icon
                icon="i-lucide-chevron-right"
                class="size-4 text-n-slate-10"
              />
            </div>
          </div>
        </section>

        <section>
          <h4 class="mb-3 text-sm font-medium text-n-slate-11">
            {{ t('FILTER.MOBILE.FILTER_BY') }}
          </h4>
          <div class="overflow-hidden rounded-xl border border-n-weak">
            <button
              v-for="category in categories"
              :key="category.key"
              type="button"
              class="flex w-full items-center gap-3 border-b border-n-weak px-3 py-4 text-left last:border-b-0"
              @click="resetSearchAndOpen(category.key)"
            >
              <Icon
                :icon="category.icon"
                class="size-4 flex-shrink-0 text-n-slate-10"
              />
              <div class="min-w-0 flex-1">
                <p class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ category.label }}
                </p>
                <p
                  v-if="category.summary"
                  class="mb-0 truncate text-sm text-n-slate-11"
                >
                  {{ category.summary }}
                </p>
              </div>
              <Icon
                icon="i-lucide-chevron-right"
                class="size-4 text-n-slate-10"
              />
            </button>
          </div>
        </section>
      </template>

      <template v-else>
        <button
          type="button"
          class="mb-5 flex items-center gap-2 text-sm font-medium text-n-slate-11"
          @click="activeView = VIEW.MAIN"
        >
          <Icon icon="i-lucide-chevron-left" class="size-4" />
          {{ t('FILTER.MOBILE.BACK') }}
        </button>

        <!-- Status -->
        <section v-if="activeView === VIEW.STATUS">
          <h4 class="mb-4 text-base font-medium text-n-slate-12">
            {{ t('FILTER.ATTRIBUTES.STATUS') }}
          </h4>
          <div class="overflow-hidden rounded-xl border border-n-weak">
            <button
              v-for="option in statusOptions"
              :key="option.id"
              type="button"
              class="flex w-full items-center justify-between gap-3 border-b border-n-weak px-3 py-4 text-left last:border-b-0"
              @click="toggleMulti(VIEW.STATUS, option)"
            >
              <span class="text-sm text-n-slate-12">{{ option.name }}</span>
              <Icon
                v-if="isMultiSelected(VIEW.STATUS, option)"
                icon="i-lucide-check"
                class="size-4 text-n-blue-11"
              />
            </button>
          </div>
        </section>

        <!-- Priority -->
        <section v-else-if="activeView === VIEW.PRIORITY">
          <h4 class="mb-4 text-base font-medium text-n-slate-12">
            {{ t('FILTER.ATTRIBUTES.PRIORITY') }}
          </h4>
          <div class="overflow-hidden rounded-xl border border-n-weak">
            <button
              v-for="option in priorityOptions"
              :key="option.id"
              type="button"
              class="flex w-full items-center justify-between gap-3 border-b border-n-weak px-3 py-4 text-left last:border-b-0"
              @click="toggleMulti(VIEW.PRIORITY, option)"
            >
              <span class="text-sm text-n-slate-12">{{ option.name }}</span>
              <Icon
                v-if="isMultiSelected(VIEW.PRIORITY, option)"
                icon="i-lucide-check"
                class="size-4 text-n-blue-11"
              />
            </button>
          </div>
        </section>

        <!-- Labels -->
        <section v-else-if="activeView === VIEW.LABELS">
          <h4 class="mb-4 text-base font-medium text-n-slate-12">
            {{ t('FILTER.ATTRIBUTES.LABELS') }}
          </h4>
          <input
            v-model="searchQuery"
            type="search"
            class="reset-base mb-3 h-10 w-full appearance-none rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="t('FILTER.MOBILE.SEARCH')"
          />
          <div class="overflow-hidden rounded-xl border border-n-weak">
            <button
              v-for="label in filteredLabels"
              :key="label.id"
              type="button"
              class="flex w-full items-center justify-between gap-3 border-b border-n-weak px-3 py-4 text-left last:border-b-0"
              @click="toggleMulti(VIEW.LABELS, label)"
            >
              <span class="text-sm text-n-slate-12">{{ label.name }}</span>
              <Icon
                v-if="isMultiSelected(VIEW.LABELS, label)"
                icon="i-lucide-check"
                class="size-4 text-n-blue-11"
              />
            </button>
          </div>
        </section>

        <!-- Assignee -->
        <section v-else-if="activeView === VIEW.ASSIGNEE">
          <h4 class="mb-4 text-base font-medium text-n-slate-12">
            {{ t('FILTER.ATTRIBUTES.ASSIGNEE_NAME') }}
          </h4>
          <input
            v-model="searchQuery"
            type="search"
            class="reset-base mb-3 h-10 w-full appearance-none rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="t('FILTER.MOBILE.SEARCH')"
          />
          <div class="overflow-hidden rounded-xl border border-n-weak">
            <button
              v-for="agent in filteredAssignees"
              :key="agent.id"
              type="button"
              class="flex w-full items-center justify-between gap-3 border-b border-n-weak px-3 py-4 text-left last:border-b-0"
              @click="setSingle(VIEW.ASSIGNEE, agent)"
            >
              <span class="text-sm text-n-slate-12">{{ agent.name }}</span>
              <Icon
                v-if="selectedAssignee?.id === agent.id"
                icon="i-lucide-check"
                class="size-4 text-n-blue-11"
              />
            </button>
          </div>
        </section>

        <!-- Team -->
        <section v-else-if="activeView === VIEW.TEAM">
          <h4 class="mb-4 text-base font-medium text-n-slate-12">
            {{ t('FILTER.ATTRIBUTES.TEAM_NAME') }}
          </h4>
          <input
            v-model="searchQuery"
            type="search"
            class="reset-base mb-3 h-10 w-full appearance-none rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="t('FILTER.MOBILE.SEARCH')"
          />
          <div class="overflow-hidden rounded-xl border border-n-weak">
            <button
              v-for="team in filteredTeams"
              :key="team.id"
              type="button"
              class="flex w-full items-center justify-between gap-3 border-b border-n-weak px-3 py-4 text-left last:border-b-0"
              @click="setSingle(VIEW.TEAM, team)"
            >
              <span class="text-sm text-n-slate-12">{{ team.name }}</span>
              <Icon
                v-if="selectedTeam?.id === team.id"
                icon="i-lucide-check"
                class="size-4 text-n-blue-11"
              />
            </button>
          </div>
        </section>

        <!-- Inbox -->
        <section v-else-if="activeView === VIEW.INBOX">
          <h4 class="mb-4 text-base font-medium text-n-slate-12">
            {{ t('FILTER.ATTRIBUTES.INBOX_NAME') }}
          </h4>
          <input
            v-model="searchQuery"
            type="search"
            class="reset-base mb-3 h-10 w-full appearance-none rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="t('FILTER.MOBILE.SEARCH')"
          />
          <div class="overflow-hidden rounded-xl border border-n-weak">
            <button
              v-for="inbox in filteredInboxes"
              :key="inbox.id"
              type="button"
              class="flex w-full items-center justify-between gap-3 border-b border-n-weak px-3 py-4 text-left last:border-b-0"
              @click="setSingle(VIEW.INBOX, inbox)"
            >
              <span class="text-sm text-n-slate-12">{{ inbox.name }}</span>
              <Icon
                v-if="selectedInbox?.id === inbox.id"
                icon="i-lucide-check"
                class="size-4 text-n-blue-11"
              />
            </button>
          </div>
        </section>

        <!-- Created at / Last activity (date presets) -->
        <section
          v-else-if="
            activeView === VIEW.CREATED_AT ||
            activeView === VIEW.LAST_ACTIVITY_AT
          "
        >
          <h4 class="mb-4 text-base font-medium text-n-slate-12">
            {{
              activeView === VIEW.CREATED_AT
                ? t('FILTER.ATTRIBUTES.CREATED_AT')
                : t('FILTER.ATTRIBUTES.LAST_ACTIVITY')
            }}
          </h4>
          <div class="overflow-hidden rounded-xl border border-n-weak">
            <button
              v-for="preset in DATE_PRESETS"
              :key="preset.value"
              type="button"
              class="flex w-full items-center justify-between gap-3 border-b border-n-weak px-3 py-4 text-left last:border-b-0"
              @click="setDatePreset(activeView, preset)"
            >
              <span class="text-sm text-n-slate-12">
                {{ formatDatePreset(preset) }}
              </span>
              <Icon
                v-if="selectedDatePreset(activeView)?.value === preset.value"
                icon="i-lucide-check"
                class="size-4 text-n-blue-11"
              />
            </button>
          </div>
        </section>

        <!-- Advanced -->
        <section v-else-if="activeView === VIEW.ADVANCED">
          <h4 class="mb-2 text-base font-medium text-n-slate-12">
            {{ t('FILTER.MOBILE.ADVANCED_FILTERS') }}
          </h4>
          <p class="mb-4 text-sm leading-6 text-n-slate-11">
            {{ t('FILTER.MOBILE.ADVANCED_DESCRIPTION') }}
          </p>
          <div
            v-if="unknownFilters.length"
            class="overflow-hidden rounded-xl border border-n-weak"
          >
            <div
              v-for="filter in unknownFilters"
              :key="`${filter.attributeKey}-${filter.filterOperator}`"
              class="flex items-center gap-3 border-b border-n-weak px-3 py-4 last:border-b-0"
            >
              <div class="min-w-0 flex-1">
                <p class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ filter.attributeKey }}
                </p>
                <p class="mb-0 truncate text-sm text-n-slate-11">
                  {{ filter.filterOperator }}
                </p>
              </div>
              <Button
                icon="i-lucide-x"
                color="slate"
                variant="ghost"
                size="sm"
                type="button"
                class="!rounded-full"
                @click="removeFilter(filter.attributeKey)"
              />
            </div>
          </div>
        </section>
      </template>
    </main>

    <footer
      class="-mx-4 md:mx-0 border-t border-n-weak bg-n-solid-2 px-4 md:px-0 pt-4"
    >
      <div class="flex gap-3">
        <Button
          class="w-full"
          color="slate"
          variant="faded"
          :label="t('FILTER.MOBILE.RESET')"
          @click="clearAll"
        />
        <Button
          class="w-full"
          :label="
            isFolderView
              ? t('FILTER.UPDATE_BUTTON_LABEL')
              : t('FILTER.MOBILE.SHOW_CONVERSATIONS')
          "
          :disabled="isFolderView && !folderNameLocal"
          @click="applyFilters"
        />
      </div>
    </footer>
  </div>
</template>

<style scoped>
@media (max-width: 767px) {
  footer {
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 1rem);
  }
}
@media (min-width: 768px) {
  footer {
    padding-bottom: 1.5rem;
  }
}
</style>
